import { useMemo } from "react";
import { Sparkles, Mail, IndianRupee, Clock, ShieldCheck, Layers, Brain } from "lucide-react";
import { env } from "../../lib/env";
import { PROJECTS, type ProjectSummary } from "../../data/projects";
import { DESIGNER_RULES } from "../../data/designerRules";
import { cls } from "../../lib/cls";
import { formatCompactMoney } from "../../lib/i18n/format";

/* LinkedProjectReasoning — the panel shown on cockpit surfaces
 * (scenarios / activity / rules / agent) when the active project
 * is linked from Foyr Neo and its bundle hasn't ingested yet.
 *
 * We DO NOT fabricate scene data. We DO reason from what's already
 * typed: the metadata the designer entered + the studio's PROJECTS
 * roster + the typed DESIGNER_RULES library. Every chip and line
 * cites the source it derived from. */

interface BundlePendingProps {
  /** Active project meta — the values the designer typed at import. */
  project: ProjectSummary;
  /** What this panel would normally render. Drives the header copy. */
  surface: "scenarios" | "activity" | "rules" | "agent";
}

const SURFACE_TITLE: Record<BundlePendingProps["surface"], string> = {
  scenarios: "Scenarios will replay against your real Foyr scene",
  activity:  "Activity will surface here once the agent walks your scene",
  rules:     "Rules will report fired/clean against your real Foyr items",
  agent:     "Reasoning trace will cite your real Foyr entities",
};

function inrL(n: number): string { return formatCompactMoney(n); }
function avg(xs: number[]): number { return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0; }

interface ReasoningTile {
  id: string;
  icon: JSX.Element;
  title: string;
  body: string;
  band: "ok" | "watch" | "info";
  cite: string;
}

const TONE: Record<ReasoningTile["band"], string> = {
  ok:    "border-fern/30 bg-fern/5 text-fern",
  watch: "border-foyr/30 bg-foyr/5 text-foyr-deep",
  info:  "border-sky-200 bg-sky-50 text-sky-800",
};

function deriveTiles(project: ProjectSummary): ReasoningTile[] {
  const tiles: ReasoningTile[] = [];

  // 1) Budget context — compare against studio's projects with the
  //    same BHK band when available, else against all projects.
  const sameBhk = PROJECTS.filter((p) => p.bhk === project.bhk && p.budgetINR > 0);
  const pool = sameBhk.length >= 2 ? sameBhk : PROJECTS.filter((p) => p.budgetINR > 0);
  if (project.budgetINR > 0 && pool.length > 0) {
    const studioAvg = avg(pool.map((p) => p.budgetINR));
    const ratio = project.budgetINR / studioAvg;
    const band: ReasoningTile["band"] = ratio < 0.7 ? "watch" : ratio > 1.4 ? "info" : "ok";
    tiles.push({
      id: "budget",
      icon: <IndianRupee className="w-3.5 h-3.5" />,
      title: `Budget · ${inrL(project.budgetINR)} ${band === "ok" ? "·" : band === "watch" ? "tight for" : "premium for"} ${project.bhk || "this size"}`,
      body: `Your studio's average for ${sameBhk.length >= 2 ? `${project.bhk} projects` : "all projects"} is ${inrL(studioAvg)} (n=${pool.length}). This brief is ${Math.round(ratio * 100)}% of that.`,
      band,
      cite: `Derived from ${pool.length} typed PROJECTS rows.`,
    });
  }

  // 2) Timeline math — days to handover from today.
  if (project.deadline) {
    const days = Math.round((new Date(project.deadline).getTime() - Date.now()) / 86_400_000);
    if (Number.isFinite(days)) {
      const band: ReasoningTile["band"] = days < 60 ? "watch" : days > 180 ? "info" : "ok";
      tiles.push({
        id: "timeline",
        icon: <Clock className="w-3.5 h-3.5" />,
        title: `Handover · ${days} d out`,
        body: days < 60
          ? `Under 60 days from today. Long-lead items (catalog says > ${env.VITE_LEAD_TIME_BUFFER_DAYS} d lead) get auto-flagged once the bundle ingests.`
          : days > 180
            ? `Over 6 months ahead. Rule library can pre-warn vendor SLAs before the curve compresses.`
            : `Within the typical 60–180-day window. Standard milestone watching kicks in once the bundle lands.`,
        band,
        cite: `From the deadline you entered + VITE_LEAD_TIME_BUFFER_DAYS env.`,
      });
    }
  }

  // 3) Likely rules to fire — pre-rank from the typed rule library
  //    by which categories typically engage at this BHK/budget tier.
  const tier =
    project.budgetINR <= 0                                          ? "unknown" :
    project.budgetINR < env.VITE_TIER_BAND_PREMIUM_INR              ? "standard" :
    project.budgetINR < env.VITE_TIER_BAND_LUXURY_INR               ? "premium"  :
                                                                       "luxury";
  const relevantCats: Array<{ cat: string; reason: string }> = [
    { cat: "budget",   reason: "Budget guardrails fire on every save." },
    { cat: "timeline", reason: "Lead-time vs milestone is universal — fires the moment any long-lead SKU enters." },
    ...(tier === "luxury"   ? [{ cat: "vendor",   reason: "Luxury tier — vendor on-time SLA matters more." }] : []),
    ...(tier === "standard" ? [{ cat: "spatial",  reason: "Standard tier — per-room area constraints are the common pinch." }] : []),
    { cat: "approval", reason: "Approval aging fires the moment client SLA crosses the threshold." },
  ];
  const rulesPicked = relevantCats
    .map((r) => ({
      ...r,
      rules: DESIGNER_RULES.filter((d) => d.category === (r.cat as typeof d.category)),
    }))
    .filter((r) => r.rules.length > 0)
    .slice(0, 3);

  if (rulesPicked.length > 0) {
    tiles.push({
      id: "rules",
      icon: <ShieldCheck className="w-3.5 h-3.5" />,
      title: `${rulesPicked.length} rule categor${rulesPicked.length === 1 ? "y" : "ies"} pre-staged for this brief`,
      body: rulesPicked.map((r) => `${r.cat} (${r.rules.length} rules) — ${r.reason}`).join("  "),
      band: "info",
      cite: `Picked from DESIGNER_RULES against tier=${tier}, bhk=${project.bhk || "—"}.`,
    });
  }

  // 4) What unlocks — concrete next step.
  tiles.push({
    id: "unlock",
    icon: <Layers className="w-3.5 h-3.5" />,
    title: "What unlocks the live picture",
    body: "Once Foyr partnership wires our companion script into their editor, neoEditor.projectExporter.export() returns the typed bundle (rooms, items, placements, materials, render history). The cockpit then re-renders end-to-end against your real scene.",
    band: "info",
    cite: "Integration surface verified live 2026-05-17 — see foyr-integration-spec.json.",
  });

  return tiles;
}

export function BundlePending({ project, surface }: BundlePendingProps): JSX.Element {
  const tiles = useMemo(() => deriveTiles(project), [project]);

  return (
    <section className="panel-hero p-6 sm:p-7 relative overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-25 pointer-events-none" />
      <div className="absolute -top-16 -right-12 w-72 h-72 rounded-full bg-foyr/10 blur-3xl pointer-events-none" />
      <div className="relative">
        <header className="flex items-start gap-3 mb-4">
          <span className="w-10 h-10 rounded-xl bg-ink-950 text-cream grid place-items-center shrink-0">
            <Brain className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <div className="eyebrow">Reasoning from what we know</div>
            <div className="display-3 text-ink-950 mt-0.5 leading-tight">
              {SURFACE_TITLE[surface]}.
            </div>
            <p className="text-[13px] text-ink-700 mt-1.5 max-w-2xl leading-snug">
              <span className="font-semibold text-ink-950">{project.name}</span> is linked from Foyr Neo. We won't fake scene-level data we don't have — but the agent <em>can</em> reason from your typed metadata + the studio's history right now. Below: four reasoning tiles, every line citing its source.
            </p>
          </div>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {tiles.map((t) => (
            <article key={t.id} className={cls("rounded-2xl border bg-cream px-4 py-3.5", TONE[t.band].replace("bg-fern/5", "border-fern/30").replace("bg-foyr/5", "border-foyr/30").replace("bg-sky-50", "border-sky-200"))}>
              <div className="flex items-center gap-2">
                <span className={cls("inline-flex w-6 h-6 rounded-lg items-center justify-center border", TONE[t.band])}>{t.icon}</span>
                <div className="text-[13px] font-semibold text-ink-950 leading-tight">{t.title}</div>
              </div>
              <div className="text-[12px] text-ink-700 mt-1.5 leading-snug">{t.body}</div>
              <div className="text-[10.5px] text-ink-500 italic mt-1.5">{t.cite}</div>
            </article>
          ))}
        </div>

        <div className="mt-4 rounded-xl border border-foyr/30 bg-foyr/5 px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[12px] text-ink-700 leading-snug">
            <Sparkles className="inline w-3 h-3 text-foyr-deep mr-1" />
            Once Foyr's bundle ingests, every tile above replaces with project-derived equivalents — real rooms, real SKUs, real alerts, with provenance.
          </div>
          <a className="pill bg-foyr text-cream inline-flex items-center gap-1" href={`mailto:${env.VITE_CONTACT_EMAIL}?subject=Foyr%20Neo%20bundle%20connector`}>
            <Mail className="w-3 h-3" /> Share an export
          </a>
        </div>
      </div>
    </section>
  );
}
